/**
 * Export Helpers
 *
 * Generic utilities for exporting data to various formats
 *
 * @module utils/exportHelpers
 */

/**
 * Download CSV file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename (without extension)
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Leading characters that Excel/Sheets can interpret as the start of a
// formula (CSV/formula injection). Cells starting with any of these are
// prefixed with a single quote so they are always read back as plain text.
const FORMULA_INJECTION_PREFIXES = ['=', '+', '-', '@'];

/**
 * Neutralize a value against CSV/formula injection (RFC-agnostic Excel/Sheets
 * mitigation): if it begins with =, +, -, or @, prefix it with a leading
 * single quote so spreadsheet apps treat it as text instead of a formula.
 * @param {string} stringValue
 * @returns {string}
 */
const guardFormulaInjection = (stringValue) => {
  if (stringValue.length > 0 && FORMULA_INJECTION_PREFIXES.includes(stringValue[0])) {
    return `'${stringValue}`;
  }
  return stringValue;
};

/**
 * Convert array of objects to CSV
 * @param {Array} data - Array of objects
 * @param {Array} headers - Array of header objects { key, label }
 * @returns {string} CSV string
 */
export const arrayToCSV = (data, headers) => {
  if (!data || data.length === 0) {
    return headers.map(h => h.label).join(',');
  }

  const headerRow = headers.map(h => h.label).join(',');
  const dataRows = data.map(row =>
    headers.map(h => {
      const value = row[h.key] ?? '';
      // Escape quotes (RFC 4180: double them) and wrap in quotes if the
      // value contains a comma, quote, or newline. Guard against CSV
      // formula injection before quoting so a leading `'` doesn't itself
      // get treated as content needing escaping.
      const stringValue = guardFormulaInjection(String(value));
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Generate filename with date
 * @param {string} prefix - Filename prefix
 * @returns {string} Filename with date
 */
export const generateFilename = (prefix) => {
  const date = new Date().toISOString().split('T')[0];
  return `${prefix}_${date}`;
};

/**
 * Download YAML file
 * @param {string} yamlContent - YAML content string
 * @param {string} filename - Filename (without extension)
 */
export const downloadYAML = (yamlContent, filename) => {
  const blob = new Blob([yamlContent], { type: 'text/yaml;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.yaml`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download Markdown file
 * @param {string} mdContent - Markdown content string
 * @param {string} filename - Filename (without extension)
 */
export const downloadMarkdown = (mdContent, filename) => {
  const blob = new Blob([mdContent], { type: 'text/markdown;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.md`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download HTML file
 * @param {string} htmlContent - HTML content string
 * @param {string} filename - Filename (without extension)
 */
export const downloadHTML = (htmlContent, filename) => {
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.html`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download JSON file
 * @param {Object} data - Data object to serialize
 * @param {string} filename - Filename (without extension)
 */
export const downloadJSON = (data, filename) => {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.json`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportHelpers = {
  downloadCSV,
  arrayToCSV,
  generateFilename,
  downloadYAML,
  downloadMarkdown,
  downloadHTML,
  downloadJSON,
};

export default exportHelpers;
